/* eslint-disable @next/next/no-img-element */
import { useLazyQuery, useQuery } from "@apollo/client";
import classNames from "classnames";
import { get, isEmpty } from "lodash";
import Link from "next/link";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import Container from "~/components/Container";
import Icon from "~/components/Icon";
import SectionTitle from "~/components/SectionTitle";
import Text from "~/components/Text";
import { Product } from "~/services/product";
import ProductsSlider from "../../components/ProductsSlider";
import styles from "./Trending.module.scss";

import "swiper/css/grid";
import { Grid } from "swiper/modules";
import { useRecoilValue } from "recoil";
import storeConfigAtom from "~/services/store-config/store-config.atom";
import {
  GET_PRODUCTS_TRACKING,
  QUERY_TRACK_VIEW_COLLECTION,
  QUERY_TRACK_VIEW_ITEM_RELATED,
  QUERY_TRACKING_IMPRESSION,
  QUERY_TRACKING_VIEW_RELATED,
} from "~/data/product/gql";
import useClient from "~/hooks/useClient";

interface TrendingProps {
  title?: string;
  image?: string;
  directLabel?: string;
  directUrl?: string;
  numberOfItems?: number;
  skip?: boolean;
  data?: Product[];
  trendingRelatedProductsBySkus?: any;
}

const Trending: FC<TrendingProps> = ({
  title = "TRENDING",
  image,
  directLabel,
  directUrl,
  // directLabel = "See All",
  // directUrl = "#",
  data,
  numberOfItems = 3.5,
  skip,
  trendingRelatedProductsBySkus,
}) => {
  const isClient = useClient();
  const { trending_group_id } = useRecoilValue(storeConfigAtom?.store);
  const trendingGroupId =
    trendingRelatedProductsBySkus?.id ?? trending_group_id;
  const [isIntersecting, setIsIntersecting] = useState(false);

  const [queryTrackViewItemRelated] = useLazyQuery(
    QUERY_TRACK_VIEW_ITEM_RELATED
  );
  const [queryTrackingViewList] = useLazyQuery(QUERY_TRACKING_VIEW_RELATED);
  const [queryTrackingViewCollection] = useLazyQuery(
    QUERY_TRACK_VIEW_COLLECTION
  );
  const [relatedTrackingImpression] = useLazyQuery(QUERY_TRACKING_IMPRESSION);

  const { data: dataCollectionTracking, loading: loadingCollectionTracking } =
    useQuery(GET_PRODUCTS_TRACKING, {
      variables: {
        skus: trendingRelatedProductsBySkus?.items?.map(
          (product: Product) => product.sku + ""
        ),
      },
      context: { fetchOptions: { method: "GET" } },
      skip:
        !trendingRelatedProductsBySkus?.items ||
        !trendingRelatedProductsBySkus?.items.length,
    });

  const _list = useMemo(() => {
    if (loadingCollectionTracking || !dataCollectionTracking)
      return trendingRelatedProductsBySkus?.items || [];
    return (trendingRelatedProductsBySkus?.items || []).map(
      (product: Product) => {
        const tracking = dataCollectionTracking?.productsBySkus?.items?.find(
          (item: any) => item.sku === product.sku
        );
        return {
          ...product,
          ...tracking,
        };
      }
    );
  }, [
    loadingCollectionTracking,
    dataCollectionTracking,
    trendingRelatedProductsBySkus,
  ]);

  const _image: string | undefined = useMemo(() => {
    if (!!skip) {
      return image;
    }
    if (isEmpty(data)) return image;

    const cmsBanner = get(data, "content", {}) as string;
    const img = cmsBanner?.match(/src="(.*?)"/)?.[1] || "";
    return img;
  }, [data, image, skip]);

  const handleImpression = useCallback(
    (products: any[]) => {
      console.log("handleImpressionTrending", trendingGroupId, products);
      queryTrackViewItemRelated({
        variables: {
          block_id: trendingGroupId,
          skus: products?.map((prod) => prod?.sku),
        },
      });
      relatedTrackingImpression({
        variables: {
          skus: products?.map((prod) => prod?.sku),
          block_id: trendingGroupId,
          type: "related",
          search_query: "",
        },
      });
    },
    [trendingGroupId]
  );

  const handleOnEnter = () => {
    console.log("queryTrackingViewList", trendingGroupId);
    setIsIntersecting(true);
  };
  useEffect(() => {
    if (isIntersecting) {
      queryTrackingViewList({
        variables: {
          block_id: trendingGroupId,
        },
      });
      queryTrackingViewCollection({
        variables: {
          block_id: trendingGroupId,
          type: "related",
        },
      });
    }
  }, [isIntersecting, trendingGroupId]);

  // if (!loading && isEmpty(products)) return null;

  const _numberOfItems = !!isEmpty(_image) ? 5.5 : numberOfItems;

  // if (fetchBanner.loading || loading) return null;

  // if (!products?.length && !loading && !!startFetching) {
  //     return null;
  // }

  return (
    <Container
      rootClassName={styles.trending_wrapper}
      className={styles.trending}
    >
      <SectionTitle
        text={trendingRelatedProductsBySkus?.block_title ?? title}
        action={
          directUrl ? (
            <Link
              href={directUrl}
              className="flex items-center gap-1 text-primary"
            >
              <Text type="title-1">{directLabel}</Text>
              <Icon name="arrow-up-right" />
            </Link>
          ) : undefined
        }
      />
      <div className="flex flex-col gap-8 md:flex-row lg:gap-10">
        {!isEmpty(_image) && (
          <div className={classNames(styles.banner, "hidden lg:block")}>
            <img
              className="object-cover"
              src={_image}
              alt={title || "Image"}
              height="100%"
              width="100%"
            />
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          <ProductsSlider
            listkey={trendingGroupId}
            products={_list}
            slidesPerView={_numberOfItems}
            grid={{
              rows: 1,
            }}
            modules={[Grid]}
            loading={!isClient}
            // onLoadingMore={() => handleFecthMore(_pageInfo?.current_page + 1)}
            // onBeforeIntersection={handleStartFetching}
            onEnter={handleOnEnter}
            onImpressions={handleImpression}
            hasNext={false}
            breakpoints={{
              1024: {
                slidesPerView: _numberOfItems,
                grid: {
                  rows: 2,
                  fill: "row",
                },
              },
              768: {
                slidesPerView: _numberOfItems - 2,
              },
            }}
          />
        </div>
      </div>
    </Container>
  );
};

export default Trending;
